<template>
  <div 
    class="node" 
    :class="
      [
        selected(), 
        node.name,
        hasError ? 'error' : '',
        hasWarning ? 'warning' : '',
      ] | kebab
    "
  >
    <div class="title">
      <span>
        <img class="icon" src="../assets/icons/effect-chain.svg">
        Effect Chain
        <md-tooltip 
          v-if="shouldMountComplexElements()" 
          md-direction="top"
          data-lod-complex="tooltip"
        >
          A predefined collection of Actions, Checks and Effects
        </md-tooltip>
      </span>
      <help-tooltip 
        v-if="shouldMountComplexElements() && hasWarning" 
        type="warning"
        data-lod-complex="help-tooltip"
      >
        <span v-for="warning in warnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip 
        v-if="shouldMountComplexElements() && hasError" 
        type="error"
        data-lod-complex="help-tooltip"
      >
        <span v-for="error in errors" :key="error.message">
          {{ error.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip 
        v-if="shouldMountComplexElements() && hasUpdates" 
        type="info"
        data-lod-complex="help-tooltip"
      >
        An update is available for this effect chain
      </help-tooltip>
      <div 
        v-if="shouldMountComplexElements() && editor.isAdmin" 
        class="nodeId"
        data-lod-complex="nodeId"
      >
        ID:{{ node.id }}
      </div>
    </div>

    <div 
      v-if="shouldMountComplexElements()"
      class="controls-container"
      data-lod-complex="controls-container"
    >
      <div v-for="control in controls()" :key="control.key" v-control="control" class="control" data-lod-complex="control" />
    </div>

    <div class="sockets-container">
      <div v-for="input in inputs()" :key="input.key" class="input">
        <Socket v-socket:input="input" class="socket" type="input" :socket="input.socket" />
        <div v-show="!input.showControl()" class="input-title">{{ input.name }}</div>
        <div v-show="input.showControl()" v-control="input.control" class="input-control" />
      </div>
      <div v-for="output in outputs()" :key="output.key" class="output">
        <div class="output-title">{{ output.name }}</div>
        <Socket v-socket:output="output" class="socket" type="output" :socket="output.socket" />
      </div>
    </div>
  </div>
</template>

<script>
import ReteVueRenderPlugin from 'rete-vue-render-plugin';

import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import intersection from 'lodash/intersection';
import toArray from 'lodash/toArray';

import HelpTooltip from '../../common/components/HelpTooltip';
import EFFECT_CHAIN_CONTROLS from '../EFFECT_CHAIN_CONTROLS';

export default {
  name: 'effect-chain',
  components: {
    Socket: ReteVueRenderPlugin.Socket,
    HelpTooltip,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      // ACTION_CONTROLS,
      state: {
        activeControlType: undefined,
        activeControl: null,
        readonly: false,
      },
    };
  },
  computed: {
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    hasError() {
      return this.errors && this.errors.length;
    },
    hasUpdates() {
      const { caseConf } = this.editor;
      return !!(caseConf.outdatedEcNodes && caseConf.outdatedEcNodes.map(node => node.id).includes(this.node.id))
    },
    warnings() {
      if (!this.editor.$store) {
        return [];
      }
      const list = this.editor.$store ?
        get(
          this.editor.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ) : [];
      return uniqBy(list, 'message');
    },
    errors() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`
        ),
        'message'
      );
    },
    chainableComponents() {
      return intersection(['Check', 'Effect', 'Timer', 'EffectChain'], toArray(this.editor.components.keys()));
    },
  },
  created() {
    const ExistingEffectChainControl = EFFECT_CHAIN_CONTROLS.EXISTING_EFFECT_CHAIN.control;
    const existingEffectChainControl = new ExistingEffectChainControl(this.editor, 'effectChain');
    this.node.addControl(existingEffectChainControl);
    this.$forceUpdate();
    this.editor.on('readonly', readonly => {
      this.state.readonly = readonly;
    });
  },
  methods: {
    // LOD methods
    isLodSimplified() {
      return this.node._lodState ? this.node._lodState.isSimplified : false;
    },
    shouldRenderComplexElements() {
      return !this.isLodSimplified();
    },
    hasBeenRenderedComplex() {
      return this.node._lodState ? this.node._lodState.hasBeenRenderedComplex : false;
    },
    useHybridRendering() {
      return this.node._lodState ? this.node._lodState.useHybridRendering : false;
    },
    // Hybrid rendering method: use v-if initially, then v-show after first complex render
    shouldMountComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering, mount elements if they've been rendered complex at least once
      return this.hasBeenRenderedComplex();
    },
    shouldShowComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering and elements are mounted, show/hide via CSS
      return this.hasBeenRenderedComplex() && this.shouldRenderComplexElements();
    },
  },
};
</script>

<style scoped>
.node {
  background: rgb(56, 180, 194);
  border-radius: 10px;
  cursor: pointer;
  min-width: 300px;
  height: auto;
  padding-bottom: 6px;
  box-sizing: content-box;
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  box-sizing: border-box;
}
.node:hover {
  background: rgba(56, 180, 194, 0.8);
}
.node.highlighted {
  background: #FBD430;
  border-color: #FBD430;
}
.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
}
.node .icon {
  width: 16px;
  height: 16px;
  display: inline;
}
.node .title {
  color: white;
  font-family: sans-serif;
  font-size: 18px;
  padding: 8px;
}
.node .nodeId {
  float: right;
}
.sockets-container {
  display: flex;
  justify-content: space-between;
}
.sockets-container {
  display: flex;
  justify-content: space-between;
}
.node .socket.input {
  background: url("../assets/icons/active-input.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-left: 0;
}
.node .socket.output {
  background: url("../assets/icons/active-output.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-right: 0;
}
.node .socket {
  border: none;
}
.node .socket:hover {
  border: solid;
}
.node .output {
  text-align: right;
}
.node .input {
  text-align: left;
}
.node .input-title,
.node .output-title {
  vertical-align: middle;
  color: white;
  display: inline-block;
  font-family: sans-serif;
  font-size: 14px;
  margin: 6px;
  line-height: 24px;
}
.node .input-control {
  z-index: 1;
  width: calc(100% - 36px);
  vertical-align: middle;
  display: inline-block;
}
.node .control {
  padding: 6px 18px;
}
.node.warning {
  border: solid 3px #f2994a;
}
.node.error {
  border: solid 3px #f92c2d;
}
</style>
